import React, { createContext, useContext, ReactNode } from 'react'
import { AwsRum, AwsRumConfig } from 'aws-rum-web'

const applicationId = import.meta.env.VITE_AWS_RUM_APPLICATION_ID || ''
const region = import.meta.env.VITE_AWS_RUM_REGION || 'eu-west-1'
const version = import.meta.env.VITE_AWS_RUM_APP_VERSION || '1.0.0'
const identityPoolId = import.meta.env.VITE_AWS_RUM_IDENTITY_POOL_ID || ''

// Define the context type
export type AwsRumContextType = AwsRum | null

// Create the context
const AwsRumContext = createContext<AwsRumContextType>(null)

interface AwsRumProviderProps {
  children: ReactNode
}

export const AwsRumProvider: React.FC<AwsRumProviderProps> = ({ children }) => {
  const [awsRum] = React.useState<AwsRum | null>(() => {
    // Only initialize RUM if applicationId and identityPoolId are configured
    if (!applicationId) {
      console.warn('AWS RUM not initialized: VITE_AWS_RUM_APPLICATION_ID is not set')
      return null
    }

    if (!identityPoolId) {
      console.warn('AWS RUM not initialized: VITE_AWS_RUM_IDENTITY_POOL_ID is not set')
      return null
    }

    try {
      const config: AwsRumConfig = {
        sessionSampleRate: 1,
        endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
        telemetries: [
          'performance',
          'errors',
          [
            'http',
            {
              addXRayTraceIdHeader: true,
              recordAllRequests: true,
              urlsToExclude: [new RegExp(`https://dataplane.rum.${region}.amazonaws.com/.+`)],
            },
          ],
        ],
        allowCookies: true,
        enableXRay: true,
        // Cognito Identity Pool for unauthenticated access
        identityPoolId,
      }

      const rum = new AwsRum(applicationId, version, region, config)

      // CSP violations don't fire as JS errors, so RUM's errors telemetry
      // misses them. Listen explicitly and pipe them into RUM.
      document.addEventListener('securitypolicyviolation', (e) => {
        rum.recordError(new Error(`CSP violation: ${e.violatedDirective} blocked ${e.blockedURI}`))
      })

      return rum
    } catch (error) {
      console.error('AWS RUM initialization error:', error)
      return null
    }
  })

  return <AwsRumContext.Provider value={awsRum}>{children}</AwsRumContext.Provider>
}

// Hook to access the AwsRum context
export const useAwsRum = (): AwsRumContextType => {
  return useContext(AwsRumContext)
}
